<script setup>
const router = useRouter()
const config = useRuntimeConfig()

const load = useState('load', () => false)
const animeSort = useState('animeSort', () => 'date')
const searchQuery = useState('searchQuery', () => '')
const searchResult = useState('searchResult', () => [])

// Лимит отображаемых карточек
const limit = ref(4)

const { data: animes, pending } = await useAsyncData(
  'animes',
  () => $fetch(`${config.public.apiBase}/api/animes`),
  {
    getCachedData: (key) => {
      const nuxtApp = useNuxtApp()
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    }
  }
)

const allAnimes = useState('allAnimes', () => animes.value)
const user = useStrapiUser()

// Лічильник живого онлайну для кожного аніме в каталозі
const onlineCounts = useOnlineCounts()

// Картка прямого ефіру в каталозі. Питаємо той самий ендпоінт, що й /live —
// поля картки він віддає поза перевіркою доступу, тож вона видима всім
// Вимкнений ефір бекенд уже віддає з showInCatalog: false — перевірку
// дублюємо тут просто щоб умова читалася сама по собі
const { access: liveAccess } = useLiveAccess()
const showLiveCard = computed(
  () => !liveAccess.value.offline && !!liveAccess.value.showInCatalog
)

// Функция для обработки скролла
const handleScroll = () => {
  if (!import.meta.client) return
  const scrollHeight = document.documentElement.scrollHeight
  const scrollTop = document.documentElement.scrollTop
  const clientHeight = document.documentElement.clientHeight

  // Если до конца страницы осталось меньше 400px
  if (scrollHeight - scrollTop - clientHeight < 400) {
    // Увеличиваем количество отображаемых карточек
    if (searchQuery.value.length > 0) {
      if (limit.value < searchResult.value.length) {
        limit.value += 15
      }
    } else {
      if (limit.value < (animes.value?.length || 0)) {
        limit.value += 15
      }
    }
  }
}

let onlineRefreshInterval = null

// Применяет текущую сортировку/фильтр к переданному списку (используется и при
// клике по кнопке, и при повторном применении сохранённого состояния)
const applySort = (type, source) => {
  let list = source ? [...source] : []
  if (type == 'date') {
    list = list.sort((a, b) => new Date(b.date) - new Date(a.date))
  }
  if (type == 'stars') {
    list = list.sort((a, b) => b.stars - a.stars)
  }
  if (type == 'ep') {
    list = list.sort((a, b) => b.maxepisodes - a.maxepisodes)
  }
  if (type == 'online') {
    list = list.sort(
      (a, b) =>
        (onlineCounts.value[b.url] || 0) - (onlineCounts.value[a.url] || 0)
    )
  }
  if (type == 'mono') {
    list = list.filter((a) => a.privat)
  }
  return list
}

onMounted(async () => {
  if (window.innerWidth >= 1024) {
    limit.value = 15
  }
  // Переприменяем сохранённый фильтр/поиск, т.к. animes при возврате на
  // страницу подтягивается из кэша заново (raw, неотсортированным)
  if (animeSort.value !== 'date') {
    animes.value = applySort(animeSort.value, allAnimes.value)
  }
  if (searchQuery.value.length > 0) {
    searchAnime()
  }
  if (import.meta.client) {
    window.addEventListener('scroll', handleScroll)
  }
})

watch(
  animeSort,
  (val) => {
    if (onlineRefreshInterval) {
      clearInterval(onlineRefreshInterval)
      onlineRefreshInterval = null
    }
    if (val === 'online') {
      onlineRefreshInterval = setInterval(() => {
        animes.value = applySort('online', allAnimes.value)
      }, 30000)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', handleScroll)
  }
  if (onlineRefreshInterval) {
    clearInterval(onlineRefreshInterval)
  }
})

const sortAnime = (type) => {
  limit.value = 20 // Сбрасываем лимит при смене сортировки
  animeSort.value = type
  animes.value = applySort(type, allAnimes.value)
}

const searchAnime = () => {
  limit.value = 15 // Сбрасываем лимит при поиске
  let result = []
  let l = searchQuery.value.length
  if (l > 0) {
    for (var i = 0; i < animes.value.length; i++) {
      let title = animes.value[i].title
      if (title.toLowerCase().match(searchQuery.value.toLowerCase())) {
        result.push(animes.value[i])
      }
    }
  }
  searchResult.value = result
}

useSeoMeta({
  title: 'Олсіор дивиться Аніме',
  twitterTitle: 'Олсіор дивиться Аніме',
  twitterDescription: `Сайт із записами стрімів перегляду аніме Olsior'а! Зі зручним інтерфейсом і крутими фішками. Збереження прогресу перегляду, запис чату.`,
  twitterImage: 'https://anime.olsior.tv/site.jpg',
  twitterCard: 'summary'
})

useHead({
  title: `Олсіор дивиться Аніме`,
  meta: [
    {
      name: 'og:title',
      hid: 'og:title',
      property: 'og:title',
      content: `Олсіор дивиться Аніме`
    },
    {
      name: 'description',
      hid: 'description',
      content: `Сайт із записами стрімів перегляду аніме Olsior'а! Зі зручним інтерфейсом і крутими фішками. Збереження прогресу перегляду, запис чату.`
    },
    {
      name: 'og:description',
      hid: 'og:description',
      property: 'og:description',
      content: `Сайт із записами стрімів перегляду аніме Olsior'а! Зі зручним інтерфейсом і крутими фішками. Збереження прогресу перегляду, запис чату.`
    },
    {
      name: 'og:image',
      hid: 'og:image',
      property: 'og:image',
      content: `https://anime.olsior.tv/site.jpg`
    }
  ]
})
</script>

<template>
  <div class="min-h-screen">
    <div class="main-bg" />
    <ClientOnly><InstallBanner /></ClientOnly>
    <MyDonate />

    <div
      class="relative flex lg:flex-row flex-col lg:justify-between items-center gap-[20px] mx-auto p-[10px] w-100 max-w-[1350px]"
    >
      <!-- Search -->
      <div class="flex w-full lg:w-auto">
        <div class="relative flex items-center w-full lg:w-auto">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="left-[12px] z-10 absolute w-4 h-4 text-neutral-content/50 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            v-model="searchQuery"
            @input="searchAnime"
            type="text"
            :placeholder="`${$t('search')}...`"
            class="bg-neutral/80 py-[8px] pr-[12px] pl-[36px] border border-neutral-content/10 focus:border-primary/60 rounded-md outline-none w-full lg:w-[400px] text-neutral-content transition-colors placeholder-neutral-content/40"
          />
        </div>
      </div>

      <!-- Sort -->
      <div
        class="flex lg:justify-end items-center gap-[6px] pb-[4px] lg:pb-0 w-full overflow-x-auto"
      >
        <button
          class="bg-neutral/80 px-[16px] py-[6px] border border-neutral-content/10 hover:border-neutral-content/30 rounded-md font-medium text-neutral-content/70 hover:text-neutral-content text-sm transition-colors"
          :class="{
            'bg-primary/80 !text-primary-content border-primary/60':
              animeSort == 'date'
          }"
          @click="sortAnime('date')"
        >
          {{ $t('sortdate') }}
        </button>
        <button
          class="bg-neutral/80 px-[16px] py-[6px] border border-neutral-content/10 hover:border-neutral-content/30 rounded-md font-medium text-neutral-content/70 hover:text-neutral-content text-sm transition-colors"
          :class="{
            'bg-primary/80 !text-primary-content border-primary/60':
              animeSort == 'stars'
          }"
          @click="sortAnime('stars')"
        >
          {{ $t('sortstars') }}
        </button>
        <button
          class="bg-neutral/80 px-[16px] py-[6px] border border-neutral-content/10 hover:border-neutral-content/30 rounded-md font-medium text-neutral-content/70 hover:text-neutral-content text-sm transition-colors"
          :class="{
            'bg-primary/80 !text-primary-content border-primary/60':
              animeSort == 'ep'
          }"
          @click="sortAnime('ep')"
        >
          {{ $t('sortep') }}
        </button>
        <button
          class="flex items-center gap-1.5 bg-neutral/80 px-[16px] py-[6px] border border-neutral-content/10 hover:border-neutral-content/30 rounded-md font-medium text-neutral-content/70 hover:text-neutral-content text-sm transition-colors"
          :class="{
            'bg-primary/80 !text-primary-content border-primary/60':
              animeSort == 'online'
          }"
          @click="sortAnime('online')"
        >
          {{ $t('sortonline') }}
        </button>
        <button
          v-if="user?.privat"
          class="bg-neutral/80 px-[16px] py-[6px] border border-neutral-content/10 hover:border-neutral-content/30 rounded-md font-medium text-neutral-content/70 hover:text-neutral-content text-sm transition-colors"
          :class="{
            'bg-primary/80 !text-primary-content border-primary/60':
              animeSort == 'mono'
          }"
          @click="sortAnime('mono')"
        >
          МОНОСАБ
        </button>
      </div>
    </div>

    <!-- Результаты поиска с ограничением limit -->
    <div
      v-if="searchResult.length > 0"
      class="gap-[15px] lg:gap-[30px] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 mx-auto p-[10px] w-100 max-w-[1350px]"
    >
      <MyCard
        v-for="anime in searchResult.slice(0, limit)"
        :key="anime.id"
        :card="anime"
        :id="anime.id"
      />
    </div>

    <!-- Основной список с ограничением limit -->
    <div
      v-if="searchResult.length == 0"
      class="lg:hidden gap-[15px] lg:gap-[30px] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 mx-auto p-[10px] w-100 max-w-[1350px]"
    >
      <LiveCard
        v-if="showLiveCard"
        :title="liveAccess.title || 'Прямий ефір'"
        :poster-url="liveAccess.posterUrl"
      />
      <MyCard
        v-for="(anime, ind) in animes?.slice(0, limit)"
        :key="anime.id"
        :card="anime"
        :id="anime.id"
        :ind="ind"
      />
    </div>

    <div
      v-if="searchResult.length == 0"
      class="hidden gap-[15px] lg:gap-[30px] lg:grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 mx-auto p-[10px] w-100 max-w-[1350px]"
    >
      <LiveCard
        v-if="showLiveCard"
        :title="liveAccess.title || 'Прямий ефір'"
        :poster-url="liveAccess.posterUrl"
      />
      <MyCard
        v-for="(anime, ind) in animes?.slice(0, limit < 15 ? 15 : limit)"
        :key="anime.id"
        :card="anime"
        :id="anime.id"
        :ind="ind"
      />
    </div>
    <MyFooter />
  </div>
</template>

<style>
@keyframes marquee {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(-100%, 0);
  }
}

.marquee {
  display: block;
  overflow: hidden;
  box-sizing: border-box;
}

.marquee .donate-text {
  display: inline-block;
  width: max-content;
  padding-left: 100%;
  white-space: nowrap;
  will-change: transform;
  animation: marquee 60s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .marquee .donate-text {
    animation-iteration-count: 1;
    animation-duration: 0.01;
    width: auto;
    padding-left: 0;
  }
}
</style>
